import { MessageSquare, FileText, Search, Brain, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type KnowledgeView =
  | "chat"
  | "files"
  | "search"
  | "sidebrain"
  | "context"
  | null;

interface KnowledgeBarProps {
  activeView: KnowledgeView;
  onViewChange: (view: KnowledgeView) => void;
}

export function KnowledgeBar({ activeView, onViewChange }: KnowledgeBarProps) {
  const topActivities = [
    { id: "chat" as const, icon: MessageSquare, label: "Chat" },
    { id: "files" as const, icon: FileText, label: "Files" },
    { id: "sidebrain" as const, icon: Brain, label: "SideBrain" },
    { id: "context" as const, icon: Database, label: "Context" },
  ];

  const bottomActivities = [
    { id: "search" as const, icon: Search, label: "Search" },
  ];

  const handleClick = (id: KnowledgeView) => {
    // Toggle: if clicking the same view, close the panel
    onViewChange(activeView === id ? null : id);
  };

  const renderActivity = (activity: {
    id: KnowledgeView;
    icon: React.ComponentType<any>;
    label: string;
  }) => {
    const Icon = activity.icon;
    const isActive = activeView === activity.id;

    return (
      <Tooltip key={activity.id}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleClick(activity.id)}
            className={`w-9 h-9 ${
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>{activity.label}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="w-12 h-full bg-card border-l flex flex-col items-center py-2 justify-between">
      <TooltipProvider>
        <div className="flex flex-col gap-1">
          {topActivities.map(renderActivity)}
        </div>
        <div className="flex flex-col gap-1">
          {bottomActivities.map(renderActivity)}
        </div>
      </TooltipProvider>
    </div>
  );
}
